import {
  Accordion,
  Box,
  HStack,
  Heading,
  IconButton,
  VStack,
  useAccordionItemContext,
  useDisclosure
} from "@chakra-ui/react"
import { XCircleIcon, type IconName } from "@pathwright/pathicons"
import useHoverCardOrPopover from "@pathwright/ux/src/components/chakra/useHoverCardOrPopover"
import { exhaustiveGuard } from "@pathwright/ux/src/utils/typing"
import Pathicon from "@pathwright/features/src/modules/pathicon/Pathicon"
import { CSSProperties, isValidElement, useEffect } from "react"
import SuggestedAction from "../path/sync/SuggestedAction"
import { Tip as TipType } from "./types"
import useTipStatus from "./useTipStatus"

export type TipProps = {
  tip: TipType
  layout?:
    | "accordion"
    | "dismissable"
    | "tooltip"
    | "accordion-pill"
    | "default"
  style?: CSSProperties
  icon?: IconName | JSX.Element | null
  defaultStatus?: "expanded" | "collapsed"
  renderButton?: (props: { onDismiss: () => void }) => JSX.Element
  colorPalette?: "light" | "dark"
}

type TipContentProps = TipProps & {
  hideTitle?: boolean
}

const TipContent = (props: TipContentProps) => {
  const { tip, icon = "lightbulb-on", style, hideTitle } = props

  const [_tipStatus, setTipStatus] = useTipStatus(tip.id!)

  return (
    <VStack
      className="tip-content"
      align="start"
      px={4}
      pb={4}
      pt={hideTitle ? 0 : 4}
      style={{ ...style }}
      gap={3}
      borderRadius="20px"
      css={{
        ["& a:not(.chakra-link)"]: {
          textDecoration: "underline"
        }
      }}
    >
      <HStack gap={3} align="center">
        {icon && (
          <HStack align="center">
            {typeof icon === "string" ? (
              <Pathicon icon={icon} color={style!.color} size="1.4em" />
            ) : (
              icon
            )}
          </HStack>
        )}
        {!hideTitle && (
          <Heading
            as="h4"
            color={style!.color}
            fontSize="md"
            fontWeight="bold"
            mt={0}
            mb={0}
          >
            {tip.title}
          </Heading>
        )}
      </HStack>
      <Box
        fontSize="sm"
        color={style!.color}
        my={0}
        css={{
          "& *": {
            color: "inherit"
          }
        }}
      >
        {typeof tip.content === "string" ? (
          <Box dangerouslySetInnerHTML={{ __html: tip.content }} />
        ) : typeof tip.content === "function" ? (
          tip.content({})
        ) : isValidElement(tip.content) ? (
          tip.content
        ) : null}
      </Box>
      {props.renderButton && (
        <HStack justify="center" w="full" pt={3} pb={0}>
          {props.renderButton({
            onDismiss: () => setTipStatus("dismissed")
          })}
        </HStack>
      )}
    </VStack>
  )
}

const TipAccordionButton = (
  props: TipProps & {
    pill?: boolean
  }
) => {
  const { tip, icon, style, pill } = props
  const { title } = tip

  const [tipStatus, setTipStatus] = useTipStatus(tip.id!)

  const item = useAccordionItemContext()
  const handleToggleTip = (toggle: boolean) => {
    setTipStatus(toggle ? "expanded" : "collapsed")
  }

  if (pill) {
    return (
      <Accordion.ItemTrigger
        justifyContent="center"
        cursor="pointer"
        onClick={() => handleToggleTip(!item.expanded)}
        pt={3}
        pb={3}
        css={{
          "& .chakra-button": {
            border: "none",
            color: "var(--chakra-colors-text-on-light)",
            fontWeight: "400"
          },
          "& svg": {
            fill: "var(--chakra-colors-text-on-light)"
          }
        }}
        _hover={{
          "& .chakra-button": {
            backgroundColor: item.expanded
              ? "transparent"
              : "rgba(0, 0, 0, 0.1)",
            color: "var(--chakra-colors-text-on-light)"
          },
          "& svg": {
            color: "var(--chakra-colors-text-on-light)"
          }
        }}
        pos="relative"
        {...(item.expanded
          ? {
              w: "100%"
            }
          : {
              w: "auto",
              m: "0 auto"
            })}
      >
        {!!icon && (
          <SuggestedAction
            // Do not nest buttons inside the Accordion.ItemTrigger!
            as="div"
            icon={icon}
            label={title}
          />
        )}
        <Box
          position="absolute"
          right={3}
          top="50%"
          transform="translateY(-50%)"
        >
          {item.expanded ? (
            <XCircleIcon
              size="1em"
              color="var(--chakra-colors-text-on-light)"
            />
          ) : null}
        </Box>
      </Accordion.ItemTrigger>
    )
  }

  return (
    <Accordion.ItemTrigger
      justifyContent="space-between"
      cursor="pointer"
      onClick={() => handleToggleTip(!item.expanded)}
      pt={3}
      pb={3}
    >
      <HStack gap={0} flex={1}>
        <Box mr={3}>
          {typeof icon === "string" ? (
            <Pathicon
              icon={icon}
              color={style!.color}
              size="1.4em"
              style={{
                marginTop: "0.1em"
              }}
            />
          ) : (
            icon
          )}
        </Box>
        <Heading textAlign="left" as="h4" fontSize="md" fontWeight="bold" m={0}>
          {title}
        </Heading>
      </HStack>
      <Pathicon
        className="toggle-tip-icon"
        icon={item.expanded ? "minus" : "plus"}
        size="1.4em"
        color={style!.color}
      />
    </Accordion.ItemTrigger>
  )
}

const TipAccordion = (
  props: TipProps & {
    pill?: boolean
  }
) => {
  const { tip, defaultStatus, pill } = props

  const [tipStatus, setTipStatus] = useTipStatus(tip.id!)

  const { open, onClose, onOpen } = useDisclosure({
    defaultOpen: (tipStatus || defaultStatus) !== "collapsed"
  })

  useEffect(() => {
    // A reset or failed first save clears the status. Apply the same default
    // used on mount instead of keeping the failed optimistic disclosure state.
    const status = tipStatus ?? defaultStatus ?? "expanded"
    if (status === "expanded") {
      onOpen()
    } else {
      onClose()
    }
  }, [tipStatus, defaultStatus, onOpen, onClose])

  // Pulling out backgroundColor to manage above TipContent, overriding style
  // prop passed to TipContent (with backgroundColor removed)
  const { backgroundColor, ...style } = props.style || {}

  return (
    <Accordion.Root value={open ? ["0"] : []}>
      <Accordion.Item
        value="0"
        border={pill ? "none" : undefined}
        bg={open ? backgroundColor : "transparent"}
        transition="background-color 0.2s ease-in-out"
        borderRadius="card"
      >
        <TipAccordionButton {...props} />
        <Accordion.ItemContent p={0}>
          <TipContent {...props} style={style} hideTitle={true} icon={null} />
        </Accordion.ItemContent>
      </Accordion.Item>
    </Accordion.Root>
  )
}

const TipDismissable = (props: TipProps) => {
  const { tip, style } = props
  const [tipStatus, setTipStatus] = useTipStatus(tip.id!)

  const handleDismiss = () => {
    setTipStatus("dismissed")
  }

  return tipStatus !== "dismissed" ? (
    <Box boxShadow="md" position="relative" borderRadius="20px" w="100%">
      <Pathicon
        className="dismiss-tip"
        icon="x-circle-fill"
        size="1.2em"
        color={style!.color}
        position="absolute"
        top={4}
        right={4}
        cursor="pointer"
        onClick={handleDismiss}
      />
      <TipContent {...props} />
    </Box>
  ) : null
}

const TipTooltip = (props: TipProps) => {
  const { tip, style } = props
  const [tipStatus, setTipStatus] = useTipStatus(tip.id!)

  const { open, onClose, onOpen } = useDisclosure({
    defaultOpen: tipStatus !== "seen"
  })

  function handleOpen() {
    setTipStatus("seen")
    onOpen()
  }

  const HoverCardOrPopover = useHoverCardOrPopover()

  return (
    <HoverCardOrPopover.Root
      open={open}
      positioning={{ placement: "left" }}
      onOpenChange={(e: { open: boolean }) =>
        e.open ? handleOpen() : onClose()
      }
    >
      <HoverCardOrPopover.Trigger asChild>
        <IconButton
          p={0}
          aria-label="info"
          children={
            <Pathicon icon="info-circle" size="1.2em" color={style!.color} />
          }
        />
      </HoverCardOrPopover.Trigger>
      <HoverCardOrPopover.Positioner>
        <HoverCardOrPopover.Content
          p={0}
          // border="none"
          borderRadius={0}
          bg={"rgba(255,255,255,0.9)"}
          // bg="transparent"
        >
          <HoverCardOrPopover.Arrow bg={"rgba(255,255,255,0.9)"} />
          <Box>
            <TipContent {...props} />
          </Box>
        </HoverCardOrPopover.Content>
      </HoverCardOrPopover.Positioner>
    </HoverCardOrPopover.Root>
  )
}

const getDefaults = (props: TipProps) => {
  return {
    layout: "dismissable",
    style: {
      ...(props.colorPalette === "dark"
        ? {
            backgroundColor: "rgba(26, 32, 44, 0.33)",
            color: "rgba(255, 255, 255, 0.9)"
          }
        : {
            backgroundColor: "rgba(26, 32, 44, 0.1)",
            color: "#444"
          }),
      ...props.style
    },
    colorPalette: "light",
    ...props
  } as TipProps
}

const Tip = (props: TipProps) => {
  const [_, __, loading] = useTipStatus(props.tip.id)
  // Wait to learn whether this tip was dismissed before showing its content.
  if (loading) return null

  const passedProps = getDefaults(props)
  const layout = passedProps.layout || "default"

  switch (layout) {
    case "accordion":
    case "accordion-pill":
      return (
        <TipAccordion
          {...passedProps}
          pill={props.layout === "accordion-pill"}
        />
      )
    case "dismissable":
      return (
        <TipDismissable {...passedProps}>
          <TipContent {...passedProps} />
        </TipDismissable>
      )
    case "tooltip":
      return <TipTooltip {...passedProps} />
    case "default":
      return <TipContent {...passedProps} />
    default:
      exhaustiveGuard(layout)
  }
}

export default Tip
